import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Badge } from '@/components/Badge';
import { BillSummary } from '@/components/BillSummary';
import type { BillRow } from '@/components/BillSummary';
import { cardClasses } from '@/components/Card';
import { Icon } from '@/components/Icon';
import { PageHeader } from '@/components/PageHeader';
import { LoadingRegion, Skeleton } from '@/components/Skeleton';
import { cn } from '@/lib/cn';
import type { LocationType } from '@/venue/venue';
import { formatParty, venueVocabulary } from '@/venue/venue';
import type { Cart } from './api';
import { useCustomer } from './context';
import { orderStatusView } from './wording';

export { Notice } from '@/components/Notice';
export { StickyActions } from '@/components/StickyActions';

/** The customer screens' title: the shared header with the badge beside it. */
export function PageTitle({
  children,
  aside,
  subtitle,
}: {
  children: ReactNode;
  aside?: ReactNode;
  subtitle?: ReactNode;
}) {
  return <PageHeader title={children} subtitle={subtitle} actions={aside} className="mb-4" />;
}

/** "Rahul · 3 guests" (or just the name at a seat): who this visit belongs to, under a page title. */
export function PartyLine() {
  const { session } = useCustomer();
  return <>{formatParty(session.locationType, session.displayName, session.partySize)}</>;
}

/**
 * After a few seconds of loading the screen says so, instead of staying a silent skeleton while the
 * request is retried. The retry policy itself is untouched.
 */
export function SlowHint({ afterMs = 3000 }: { afterMs?: number }) {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setSlow(true), afterMs);
    return () => window.clearTimeout(timer);
  }, [afterMs]);
  if (!slow) return null;
  return (
    <p role="status" className="mt-4 text-center text-sm text-muted">
      Still trying to load. Thanks for waiting.
    </p>
  );
}

export function ListSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <LoadingRegion>
      <div className={cn(cardClasses({ padding: 'none' }), 'divide-y divide-line')}>
        {Array.from({ length: rows }, (_, index) => (
          <div key={index} className="flex items-center gap-3 px-4 py-3">
            <div className="flex flex-1 flex-col gap-2">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-3 w-1/3" />
            </div>
            <Skeleton className="h-4 w-14" />
          </div>
        ))}
      </div>
      <SlowHint />
    </LoadingRegion>
  );
}

/** A white panel on the canvas: the one surface lists and totals sit on. */
export function Panel({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn(cardClasses({ padding: 'none' }), className)}>{children}</div>;
}

/** The cart is a draft until the customer places it. */
export function DraftBadge() {
  return <Badge tone="neutral">Draft · not ordered</Badge>;
}

/** Who put a line in the cart. The waiter badge is the one violet on the screen; "you" stays quiet. */
export function AddedByYouBadge() {
  return <Badge tone="neutral">Added by you</Badge>;
}

export function OrderStatusBadge({
  status,
  locationType,
}: {
  status: string;
  locationType: LocationType;
}) {
  const view = orderStatusView(status, locationType);
  return <Badge tone={view.tone}>{view.label}</Badge>;
}

const PROGRESS = ['SUBMITTED', 'IN_PREPARATION', 'READY', 'SERVED'] as const;

/**
 * Where a placed order is on its way: received, preparing, ready, served (delivered at a seat). The backend's
 * status picks the step; nothing is inferred. A cancelled order shows no progress.
 */
export function OrderProgress({
  status,
  locationType,
}: {
  status: string;
  locationType: LocationType;
}) {
  const index = PROGRESS.indexOf(status as (typeof PROGRESS)[number]);
  if (index === -1) return null;
  const labels = ['Received', 'Preparing', 'Ready', venueVocabulary(locationType).servedShort];
  return (
    <ol aria-label="Order progress" className="flex items-start">
      {labels.map((label, step) => {
        const done = step < index;
        const current = step === index;
        return (
          <li
            key={label}
            aria-current={current ? 'step' : undefined}
            className="relative flex flex-1 flex-col items-center gap-1.5 text-center"
          >
            {step > 0 && (
              <span
                aria-hidden="true"
                className={cn(
                  'absolute top-3 right-1/2 left-[-50%] h-0.5',
                  step <= index ? 'bg-primary' : 'bg-line-strong',
                )}
              />
            )}
            <span
              aria-hidden="true"
              className={cn(
                'relative z-[1] grid size-6 place-items-center rounded-full border-2 text-[11px] font-bold',
                done && 'border-primary bg-primary text-white',
                current && 'border-primary bg-white text-primary',
                !done && !current && 'border-line-strong bg-white text-faint',
              )}
            >
              {done ? <Icon name="check" size={14} /> : step + 1}
            </span>
            <span
              className={cn(
                'text-xs leading-tight',
                current ? 'font-semibold text-ink' : done ? 'text-muted' : 'text-faint',
              )}
            >
              {label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export function QuantityStepper({
  value,
  label,
  onChange,
  disabled = false,
  min = 1,
}: {
  value: number;
  /** What is being counted, for screen readers: "Chicken Biryani" or "guests". */
  label: string;
  onChange: (next: number) => void;
  disabled?: boolean;
  min?: number;
}) {
  const button =
    'grid size-11 place-items-center rounded-control text-ink hover:bg-surface ' +
    'disabled:cursor-not-allowed disabled:text-faint disabled:hover:bg-transparent';
  return (
    <div
      role="group"
      aria-label={`Quantity of ${label}`}
      className="inline-flex items-center rounded-control border border-line-strong bg-white"
    >
      <button
        type="button"
        className={button}
        aria-label={`Decrease quantity of ${label}`}
        disabled={disabled || value <= min}
        onClick={() => onChange(value - 1)}
      >
        <span aria-hidden="true" className="text-lg font-semibold">
          −
        </span>
      </button>
      <output
        aria-live="polite"
        className="min-w-8 text-center text-base font-bold text-ink tabular-nums"
      >
        {value}
      </output>
      <button
        type="button"
        className={button}
        aria-label={`Increase quantity of ${label}`}
        disabled={disabled}
        onClick={() => onChange(value + 1)}
      >
        <Icon name="plus" size={18} />
      </button>
    </div>
  );
}

/** Cart totals exactly as the server sent them. A service charge row appears only when the server supplies one. */
export function CartTotals({ cart }: { cart: Cart }) {
  const { money } = useCustomer();
  const rows: BillRow[] = [];
  if (cart.tax_amount !== 0) rows.push({ label: 'Tax', amount: money(cart.tax_amount) });
  if (typeof cart.service_charge_amount === 'number' && cart.service_charge_amount !== 0) {
    rows.push({ label: 'Service charge', amount: money(cart.service_charge_amount) });
  }
  return (
    <BillSummary
      subtotal={money(cart.subtotal_amount)}
      taxes={rows}
      total={money(cart.total_amount)}
    />
  );
}

export function formatTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(date);
}
